// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Shared hook for fetching analysis statuses with version tracking and debounced refresh.
 *
 * Caller passes the agents it cares about; each agent's whole result set comes
 * back in one query. Every dataset is parsed into per-docId Maps (scores,
 * versions, types, dates), addressed by (agentId, datasetKey) — a bare key is
 * ambiguous once two agents can share one.
 *
 * `refreshDocument` is the exception: a single document needs its dataset keys
 * named, so that one takes them as an argument rather than reading the agents'
 * entire result sets to keep one row from each.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { NodeApiClient } from '@/app-shell/services'
import {
  fetchAnalysisStatuses,
  fetchDocumentAnalysisStatuses,
  type AgentDataset,
  type AnalysisStatusResponse,
} from '../services/dataset'

// ---------------------------------------------------------------------------
// Utility: parse raw analysis status response into typed Maps
// ---------------------------------------------------------------------------

export interface ParsedAnalysisStatuses {
  scores: Map<string, number>
  versions: Map<string, number>
  types: Map<string, string>
  dates: Map<string, string>
  counts: Map<string, number>
}

/**
 * Parse the raw `{ "docId_vN": { s, t, d, c } }` map into per-docId Maps,
 * keeping only the highest version per docId.
 */
export function parseAnalysisStatusResponse(
  raw: Record<string, { s?: number | null; t?: string | null; d?: string | null; c?: number | null }>,
): ParsedAnalysisStatuses {
  const scores = new Map<string, number>()
  const versions = new Map<string, number>()
  const types = new Map<string, string>()
  const dates = new Map<string, string>()
  const counts = new Map<string, number>()

  for (const [key, entry] of Object.entries(raw)) {
    const lastV = key.lastIndexOf('_v')
    const docId = key.slice(0, lastV)
    const ver = parseInt(key.slice(lastV + 2), 10)
    const existingVer = versions.get(docId)
    if (existingVer == null || ver > existingVer) {
      if (entry.s != null) scores.set(docId, entry.s)
      versions.set(docId, ver)
      if (entry.t) types.set(docId, entry.t)
      if (entry.d) dates.set(docId, entry.d)
      if (entry.c != null) counts.set(docId, entry.c)
    }
  }

  return { scores, versions, types, dates, counts }
}

// ---------------------------------------------------------------------------
// Hook: all analysis statuses (single fetch, all datasets)
// ---------------------------------------------------------------------------

export interface UseAnalysisStatusesOptions {
  apiClient: NodeApiClient
  orgId: string
  workspaceId: string
  /** Agents whose results to fetch — usually those backing the active renderers. */
  agentIds: string[]
  /** Session or group ID for the query. Omit for workspaces whose docs live under the default `_` group bucket. */
  groupId?: string
  /** Set false to skip fetching */
  enabled?: boolean
}

export interface UseAnalysisStatusesResult {
  /** Get parsed statuses for one agent's dataset */
  getDataset: (agentId: string, datasetKey: string) => ParsedAnalysisStatuses
  loading: boolean
  /** Trigger a debounced full re-fetch (e.g. after a job completes) */
  requestRefresh: () => void
  /**
   * Fetch one document's statuses for the given datasets and merge them into
   * existing state. The caller names the datasets because a document is not
   * addressable on its own — see `fetchDocumentAnalysisStatuses`.
   */
  refreshDocument: (docId: string, datasets: AgentDataset[]) => void
}

const EMPTY: ParsedAnalysisStatuses = {
  scores: new Map(),
  versions: new Map(),
  types: new Map(),
  dates: new Map(),
  counts: new Map(),
}

/**
 * Fetch and parse analysis statuses for all datasets in one query.
 * Includes a debounced `requestRefresh` for coalescing rapid job completions.
 */
/** Address one agent's dataset. Bare keys collide across agents; these don't. */
function datasetRef(agentId: string, datasetKey: string): string {
  return `${agentId}::${datasetKey}`
}

/** Flatten the agent -> dataset -> entries response into a ref-keyed map. */
function parseAgents(
  agents: AnalysisStatusResponse['agents'],
): Map<string, ParsedAnalysisStatuses> {
  const map = new Map<string, ParsedAnalysisStatuses>()
  for (const [agentId, datasets] of Object.entries(agents)) {
    for (const [dsKey, entries] of Object.entries(datasets)) {
      map.set(datasetRef(agentId, dsKey), parseAnalysisStatusResponse(entries))
    }
  }
  return map
}

export function useAnalysisStatuses({
  apiClient,
  orgId,
  workspaceId,
  agentIds,
  groupId,
  enabled = true,
}: UseAnalysisStatusesOptions): UseAnalysisStatusesResult {
  const [parsed, setParsed] = useState<Map<string, ParsedAnalysisStatuses>>(new Map())
  const [loading, setLoading] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const requestRefresh = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setRefreshTrigger(n => n + 1), 2000)
  }, [])

  // Stabilize agentIds — callers often pass a fresh array each render.
  const agentIdsSerialized = useMemo(
    () => [...agentIds].sort().join('\n'),
    [agentIds],
  )

  useEffect(() => {
    if (!enabled) return
    if (agentIdsSerialized === '') {
      setParsed(new Map())
      return
    }
    const ids = agentIdsSerialized.split('\n')
    let cancelled = false
    setLoading(true)
    fetchAnalysisStatuses(apiClient, orgId, workspaceId, ids, groupId)
      .then((res) => {
        if (cancelled) return
        setParsed(parseAgents(res.agents))
      })
      .catch((err) => {
        if (!cancelled) console.warn('Failed to fetch analysis statuses:', err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [apiClient, orgId, workspaceId, agentIdsSerialized, groupId, enabled, refreshTrigger])

  const refreshDocument = useCallback((docId: string, datasets: AgentDataset[]) => {
    fetchDocumentAnalysisStatuses(apiClient, orgId, workspaceId, datasets, docId, groupId)
      .then((res) => {
        setParsed(prev => {
          const next = new Map(prev)
          for (const [ref, fresh] of parseAgents(res.agents)) {
            const existing = next.get(ref)
            if (!existing) {
              next.set(ref, fresh)
            } else {
              // Merge: overwrite entries for this docId, keep everything else
              const merged: ParsedAnalysisStatuses = {
                scores: new Map(existing.scores),
                versions: new Map(existing.versions),
                types: new Map(existing.types),
                dates: new Map(existing.dates),
                counts: new Map(existing.counts),
              }
              for (const [id, v] of fresh.scores) merged.scores.set(id, v)
              for (const [id, v] of fresh.versions) merged.versions.set(id, v)
              for (const [id, v] of fresh.types) merged.types.set(id, v)
              for (const [id, v] of fresh.dates) merged.dates.set(id, v)
              for (const [id, v] of fresh.counts) merged.counts.set(id, v)
              next.set(ref, merged)
            }
          }
          return next
        })
      })
      .catch((err) => console.warn('Failed to refresh document analysis status:', err))
  }, [apiClient, orgId, workspaceId, groupId])

  const getDataset = useCallback(
    (agentId: string, datasetKey: string) => parsed.get(datasetRef(agentId, datasetKey)) ?? EMPTY,
    [parsed],
  )

  return { getDataset, loading, requestRefresh, refreshDocument }
}
